<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 px-3 py-4">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<button
						type="button"
						class="inline-flex items-center gap-2 px-3 py-2 text-gray-500 bg-transparent border-none rounded-lg text-sm cursor-pointer transition-colors hover:bg-gray-100"
						@click="goBack"
					>
						<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
						Back to Currencies
					</button>
				</div>
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Currency</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0">Update currency properties</p>
				</div>
				<button
					type="button"
					:disabled="isSaving || !hasChanges"
					class="inline-flex items-center gap-2 px-4 py-2 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
					@click="saveCurrency"
				>
					<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Loading State -->
			<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
				<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span class="ml-3 text-gray-500 text-sm">Loading currency...</span>
			</div>

			<!-- Edit Form -->
			<div v-else class="max-w-3xl mx-auto">
				<!-- Properties Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
					<!-- Card Header -->
					<div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Currency Properties</h2>
					</div>
					<!-- Card Body -->
					<div class="p-6">
						<div class="grid gap-5">
							<!-- ID (Read-only) -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">ID (ISO Code)</label>
								<input
									:value="currency.id"
									type="text"
									disabled
									class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
								>
							</div>
							<!-- Name -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Name *</label>
								<input
									v-model="currency.name"
									type="text"
									placeholder="Enter currency name"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
								>
							</div>
							<!-- Sign -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Sign (Symbol)</label>
								<input
									v-model="currency.sign"
									type="text"
									placeholder="e.g., $, €, £"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
								>
							</div>
							<!-- Prefix -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Prefix</label>
								<input
									v-model="currency.prefix"
									type="text"
									placeholder="Text before price"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
								>
							</div>
							<!-- Suffix -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Suffix</label>
								<input
									v-model="currency.suffix"
									type="text"
									placeholder="Text after price"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
								>
							</div>
							<!-- Separator -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Separator</label>
								<input
									v-model="currency.separator"
									type="text"
									placeholder="Decimal separator (e.g., . or ,)"
									class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
								>
							</div>
							<!-- Default -->
							<div class="grid grid-cols-[10rem_1fr] items-center gap-4">
								<label class="text-sm font-medium text-gray-700">Default</label>
								<label class="flex items-center gap-2 cursor-pointer">
									<input
										v-model="currency.default"
										type="checkbox"
										:true-value="1"
										:false-value="0"
										class="w-4 h-4 accent-orange-7"
									>
									<span class="text-sm text-gray-500">Set as default currency</span>
								</label>
							</div>
						</div>
					</div>
				</div>

				<!-- Preview Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden mt-4">
					<!-- Card Header -->
					<div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Preview</h2>
					</div>
					<!-- Card Body -->
					<div class="p-6">
						<p class="text-sm text-gray-500 mb-3 mt-0">Example price display:</p>
						<div class="text-2xl font-semibold text-gray-900">
							{{ currency.prefix }}{{ currency.sign }}99{{ currency.separator || '.' }}99{{ currency.suffix }}
						</div>
					</div>
				</div>
			</div>
		</main>

		<!-- Toast Notification -->
		<div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
			<div
				class="flex items-center gap-3 px-5 py-4 rounded-lg shadow-lg"
				:class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
			>
				<svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
				</svg>
				<svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				<span class="text-sm font-medium text-white">{{ toastMessage }}</span>
				<button
					type="button"
					class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
					@click="hideToast"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import CurrencyService from 'services/CurrencyService';

export default defineComponent({
	name: 'EditCurrencyPage',
	setup() {
		const router = useRouter();
		const route = useRoute();
		const auth0 = useAuth0();

		const currency = ref({
			id: '',
			name: '',
			sign: '',
			prefix: '',
			suffix: '',
			separator: '',
			default: 0,
		});
		const originalCurrency = ref(null);
		const isLoading = ref(true);
		const isSaving = ref(false);

		const toastMessage = ref('');
		const toastVariant = ref('success');
		let toastTimeout;

		const loggedIn = computed(() => auth0.isAuthenticated.value);

		const hasChanges = computed(() => {
			if (!originalCurrency.value) return false;
			return (
				currency.value.name !== originalCurrency.value.name ||
				currency.value.sign !== originalCurrency.value.sign ||
				currency.value.prefix !== originalCurrency.value.prefix ||
				currency.value.suffix !== originalCurrency.value.suffix ||
				currency.value.separator !== originalCurrency.value.separator ||
				currency.value.default !== originalCurrency.value.default
			);
		});

		const showToast = (message, variant = 'success') => {
			toastMessage.value = message;
			toastVariant.value = variant;
			if (toastTimeout) clearTimeout(toastTimeout);
			toastTimeout = setTimeout(() => {
				toastMessage.value = '';
			}, 4000);
		};

		const hideToast = () => {
			toastMessage.value = '';
			if (toastTimeout) clearTimeout(toastTimeout);
		};

		const goBack = () => {
			router.push('/currency');
		};

		const loadData = async () => {
			if (!loggedIn.value) return;

			isLoading.value = true;
			try {
				const currencyId = route.params.id;
				const data = await CurrencyService.get(currencyId);
				currency.value = {
					id: data.id || '',
					name: data.name || '',
					sign: data.sign || '',
					prefix: data.prefix || '',
					suffix: data.suffix || '',
					separator: data.separator || '',
					default: data.default || 0,
				};
				originalCurrency.value = { ...currency.value };
			} catch (error) {
				showToast(error.message || 'Failed to load currency', 'error');
			} finally {
				isLoading.value = false;
			}
		};

		const saveCurrency = async () => {
			if (!hasChanges.value) return;

			isSaving.value = true;
			try {
				await CurrencyService.update(currency.value.id, { ...currency.value });
				originalCurrency.value = { ...currency.value };
				showToast('Currency saved successfully', 'success');
			} catch (error) {
				showToast(error.message || 'Failed to save currency', 'error');
			} finally {
				isSaving.value = false;
			}
		};

		watch(loggedIn, async (isLoggedIn) => {
			if (isLoggedIn) {
				loadData();
			}
		}, { immediate: true });

		return {
			currency,
			isLoading,
			isSaving,
			hasChanges,
			toastMessage,
			toastVariant,
			goBack,
			saveCurrency,
			hideToast,
		};
	},
});
</script>
